import React, { useMemo, useState } from "react";
import "flag-icons/css/flag-icons.min.css";
import Icon from "./Icon";
import {
  COUNTRIES,
  MAX_FLAGS,
  PRIDE_FLAGS,
  FlagGroup,
  GROUP_COUNTRIES,
  PrideFlag,
  flagInfo,
  inGroup,
} from "../flags";

// A tab's flags in order: its pride flags, with any country flags placed
// after the flag named in `after` (or at the end).
function groupList(tab: FlagGroup) {
  const list = PRIDE_FLAGS.filter((f) => inGroup(f, tab)).map((f) => ({ id: `p:${f.id}`, label: f.name }));
  for (const c of GROUP_COUNTRIES[tab] || []) {
    const item = { id: `c:${c.code}`, label: c.label };
    const at = c.after ? list.findIndex((x) => x.id === `p:${c.after}`) : -1;
    if (at >= 0) list.splice(at + 1, 0, item);
    else list.push(item);
  }
  return list;
}

// Points for an n-pointed star (or a sun with n rays) centred on cx, cy.
function starPoints(cx: number, cy: number, outer: number, inner: number, n: number) {
  const pts: string[] = [];
  for (let i = 0; i < n * 2; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const a = (Math.PI * i) / n - Math.PI / 2;
    pts.push(`${(cx + r * Math.cos(a)).toFixed(2)},${(cy + r * Math.sin(a)).toFixed(2)}`);
  }
  return pts.join(" ");
}

// The infinity loop on the autistic and neurodivergent pride flags.
const INFINITY =
  "M15,10 C17,6 22,6 22,10 C22,14 17,14 15,10 C13,6 8,6 8,10 C8,14 13,14 15,10 Z";

// Pride flags drawn as SVG (3:2), stripes plus a few special shapes.
function PrideSvg({ flag }: { flag: PrideFlag }) {
  const n = flag.stripes.length;
  const clipId = `clip-${flag.id}`;
  const gradId = `grad-${flag.id}`;
  return (
    <svg viewBox="0 0 30 20" preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <clipPath id={clipId}>
          <rect width="30" height="20" />
        </clipPath>
        {flag.extra === "infinityRainbow" && (
          <linearGradient id={gradId} x1="8" y1="0" x2="22" y2="0" gradientUnits="userSpaceOnUse">
            {["#e40303", "#ff8c00", "#ffed00", "#008026", "#24408e", "#732982"].map((c, i) => (
              <stop key={c} offset={i / 5} stopColor={c} />
            ))}
          </linearGradient>
        )}
      </defs>
      <g clipPath={`url(#${clipId})`}>
        {flag.stripes.map((c, i) =>
          flag.vertical ? (
            <rect key={i} x={(i * 30) / n} y="0" width={30 / n + 0.05} height="20" fill={c} />
          ) : (
            <rect key={i} x="0" y={(i * 20) / n} width="30" height={20 / n + 0.05} fill={c} />
          )
        )}

        {flag.extra === "progress" &&
          // nested chevrons from the hoist: black, brown, light blue, pink, white
          [
            ["#000000", 10],
            ["#784f17", 7.5],
            ["#5bcefa", 5],
            ["#f5a9b8", 2.5],
            ["#ffffff", 0],
          ].map(([color, d]) => (
            <polygon
              key={color as string}
              points={`0,0 ${d},0 ${(d as number) + 10},10 ${d},20 0,20`}
              fill={color as string}
            />
          ))}

        {flag.extra === "demi" && <polygon points="0,0 10,10 0,20" fill="#000000" />}

        {flag.extra === "intersex" && (
          <circle cx="15" cy="10" r="4.6" fill="none" stroke="#7902aa" strokeWidth="1.9" />
        )}

        {flag.extra === "twoSpirit" && (
          <>
            <circle cx="15" cy="10" r="5" fill="none" stroke="#ffffff" strokeWidth="1.4" />
            {/* two feathers inside the circle */}
            <ellipse cx="13.7" cy="9.6" rx="1" ry="3.2" fill="#ffffff" transform="rotate(-18 13.7 9.6)" />
            <ellipse cx="16.3" cy="9.6" rx="1" ry="3.2" fill="#ffffff" transform="rotate(18 16.3 9.6)" />
            <path d="M13.2 12.6 L12.7 14.2 M16.8 12.6 L17.3 14.2" stroke="#ffffff" strokeWidth="0.5" />
          </>
        )}

        {flag.extra === "disability" && (
          // five parallel bands crossing diagonally from top-left to bottom-right
          <g transform="rotate(33.7 15 10)">
            {["#cf7280", "#eedf77", "#e8e8e8", "#7bc2e0", "#3aad7d"].map((c, i) => (
              <rect key={c} x="-10" y={10 - 3.75 + i * 1.5} width="50" height="1.5" fill={c} />
            ))}
          </g>
        )}

        {flag.extra === "intersexProgress" && (
          <>
            {[
              ["#000000", 12.5],
              ["#784f17", 10],
              ["#5bcefa", 7.5],
              ["#f5a9b8", 5],
              ["#ffffff", 2.5],
              ["#ffd800", 0],
            ].map(([color, d]) => (
              <polygon
                key={color as string}
                points={`0,0 ${d},0 ${(d as number) + 10},10 ${d},20 0,20`}
                fill={color as string}
              />
            ))}
            <circle cx="3.6" cy="10" r="1.7" fill="none" stroke="#7902aa" strokeWidth="0.7" />
          </>
        )}

        {flag.extra === "violet" && (
          // a little violet flower in the middle
          <>
            {[0, 72, 144, 216, 288].map((deg) => {
              const a = (deg * Math.PI) / 180;
              return (
                <circle
                  key={deg}
                  cx={15 + 1.6 * Math.sin(a)}
                  cy={10 - 1.6 * Math.cos(a)}
                  r="1.5"
                  fill="#a35dd6"
                />
              );
            })}
            <circle cx="15" cy="10" r="1" fill="#ffd800" />
          </>
        )}

        {flag.extra === "pi" && (
          <path
            d="M11.8,7.4 H18.4 M13.9,7.4 Q13.8,11 12.6,13 M16.6,7.4 V12 Q16.7,13 18,12.5"
            fill="none"
            stroke="#ffd700"
            strokeWidth="1.2"
            strokeLinecap="round"
          />
        )}

        {flag.extra === "infinityRainbow" && (
          <path d={INFINITY} fill="none" stroke={`url(#${gradId})`} strokeWidth="2.3" />
        )}

        {flag.extra === "infinityGold" && (
          <path d={INFINITY} fill="none" stroke="#e8b10b" strokeWidth="2.3" />
        )}

        {flag.extra === "deaf" && (
          <>
            <polygon points="30,4 30,20 10,20" fill="#1c4f8f" />
            {/* an open yellow hand */}
            <g fill="#ffd23f">
              <rect x="11.6" y="9" width="6" height="6.2" rx="1.6" />
              <rect x="11.7" y="4.6" width="1.3" height="5.5" rx="0.65" />
              <rect x="13.3" y="3.8" width="1.3" height="6" rx="0.65" />
              <rect x="14.9" y="4.1" width="1.3" height="6" rx="0.65" />
              <rect x="16.4" y="5.2" width="1.2" height="5" rx="0.6" />
              <rect
                x="17"
                y="10.2"
                width="1.3"
                height="4"
                rx="0.65"
                transform="rotate(-50 17.6 12.2)"
              />
            </g>
          </>
        )}

        {flag.extra === "aboriginal" && <circle cx="15" cy="10" r="5" fill="#ffd400" />}

        {flag.extra === "torresStrait" && (
          <>
            <rect x="0" y="4.4" width="30" height="11.2" fill="#000000" />
            <rect x="0" y="5" width="30" height="10" fill="#0f5bb5" />
            {/* the white dhari (headdress) above a five-pointed star */}
            <path
              d="M10.2,10 Q10.6,5.6 15,5.4 Q19.4,5.6 19.8,10 L18.4,10 Q17.8,7.4 15,7.2 Q12.2,7.4 11.6,10 Z"
              fill="#ffffff"
            />
            <polygon points={starPoints(15, 11.4, 2.5, 1, 5)} fill="#ffffff" />
          </>
        )}

        {flag.extra === "sami" && (
          <>
            <rect x="11.1" y="0" width="3.4" height="20" fill="#007229" />
            <rect x="14.5" y="0" width="3.4" height="20" fill="#ffce00" />
            <rect x="17.9" y="0" width="12.1" height="20" fill="#0035ad" />
            {/* the circle: blue half on the red, red half on the blue */}
            <path
              d="M14.5,4.6 A5.4,5.4 0 0 0 14.5,15.4"
              fill="none"
              stroke="#0035ad"
              strokeWidth="1.3"
            />
            <path
              d="M14.5,4.6 A5.4,5.4 0 0 1 14.5,15.4"
              fill="none"
              stroke="#d52b1e"
              strokeWidth="1.3"
            />
          </>
        )}

        {flag.extra === "romani" && (
          <g stroke="#d4202a" fill="none">
            <circle cx="15" cy="10" r="4.6" strokeWidth="0.8" />
            <circle cx="15" cy="10" r="0.9" fill="#d4202a" stroke="none" />
            {Array.from({ length: 16 }, (_, i) => {
              const a = (Math.PI * i) / 8;
              return (
                <line
                  key={i}
                  x1={15 + Math.cos(a)}
                  y1={10 + Math.sin(a)}
                  x2={15 + 4.6 * Math.cos(a)}
                  y2={10 + 4.6 * Math.sin(a)}
                  strokeWidth="0.45"
                />
              );
            })}
          </g>
        )}

        {flag.extra === "metis" && (
          // the white infinity sign, from a 400x300 drawing
          <path
            transform="scale(0.075 0.0667)"
            d="M290,203a60,53 0 0 0 0-106c-72,0-108,106-180,106a60,53 0 0 1 0-106c72,0 108,106 180,106z"
            fill="none"
            stroke="#ffffff"
            strokeWidth="24"
          />
        )}

        {flag.extra === "haudenosaunee" && (
          // the Hiawatha belt: four linked squares and the Tree of Peace (360x216 drawing)
          <g transform="scale(0.0833 0.0926)">
            <path fill="#fff" d="M0 103h105.5v10H0zM254.5 103H360v10H254.5zM137.8 121h83.8v10h-83.8z" />
            <path
              fill="#593eb6"
              stroke="#fff"
              strokeWidth="10"
              d="M24 85h40v45H24zM296 85h40v45h-40zM101 80h40v56h-40zM220 80h40v56h-40z"
            />
            <path
              fill="#fff"
              stroke="#fff"
              d="m179.21687 67.519005-.12213 4.597155h-2.279v5.667856h-1.9668l.12488 5.431694h-2.27901v5.397958l-2.02923.067474.21853 5.36422h-2.27901v4.959373l-1.93558-.033737-.12488 5.904012h-1.99802l-.03121 4.92564h-1.37365v5.66786h-2.52875l.31219 21.76051 2.21656-.06748.03122 5.63413h1.34243l.03121 5.43169h1.99802v5.83654h10.05257l.90535 19.53386h5.68189l.43706-19.53386h10.02135v-5.83654h1.99802l.06244-5.43169h1.3112l.03122-5.63413 2.21656.06748.31219-21.76051h-2.52875v-5.66786h-1.34242l-.03123-4.92564h-2.02923l-.12488-5.904012-1.93558.033737v-4.959373h-2.27901l.24976-5.36422-2.06046-.067474V83.21571h-2.27901l.12488-5.431694h-1.9668V72.11616h-2.279v-4.520789h-.03121z"
            />
          </g>
        )}

        {flag.extra === "mikmaq" && (
          <g fill="#ff0000">
            <rect x="6.5" y="1.2" width="3" height="17.6" />
            <rect x="0.9" y="8" width="28.2" height="4" />
            <polygon points={starPoints(3.3, 4, 2.6, 1.05, 5)} />
            {/* crescent: a red disc with a white disc over its top */}
            <circle cx="3.3" cy="15.9" r="2.6" />
            <circle cx="3.3" cy="14.6" r="2.6" fill="#ffffff" />
          </g>
        )}

        {flag.extra === "wiphala" && (
          // Qullasuyu wiphala: 7x7 squares, white diagonal (7x7 drawing)
          <g transform="scale(4.2857 2.8571)">
            <path d="M0,2V4L3,7H5M5,0H3L7,4V2" fill="#742c64" />
            <path d="M1,0H3L7,4V6M1,5H0V7H3" fill="#eb7711" />
            <path d="M1,0H2V2H4V4H6V6H7V5H5V3H3V1H1M0,6H1V7H0" fill="#fcdd09" />
            <path d="M0,1V2L5,7H6V6H5V5H4V4H3V3H2V2H1V1M5,0H7V2" fill="#078930" />
            <path d="M0,2H1V4H3V6H5V7H4V5H2V3H0M5,0H6V2H7V1H5" fill="#0f47af" />
            <path d="M3,0H4V2H6V4H7V3H5V1H3M0,4H1V6H3V7H2V5H0" fill="#da121a" />
          </g>
        )}

        {flag.extra === "tinoRangatiratanga" && (
          // black above, red below, a white koru between (1080x600 drawing)
          <g transform="scale(0.02778 0.03333)">
            <path d="M0,80h300a135,135 0 0,1 0,270h780V0H0" fill="#000000" />
            <path
              d="M0,190h300a80,80 0 0,1 0,160a55,55 0 0,0 0-110a110,110 0 0,0 0,220h780v140H0"
              fill="#d40000"
            />
          </g>
        )}

        {flag.extra === "juneteenth" && (
          <>
            {/* red ground under a curved horizon, a nova burst, and a star */}
            <path d="M0,14 Q15,6 30,14 V20 H0 Z" fill="#c8102e" />
            <polygon
              points={starPoints(15, 9.6, 5, 2.8, 12)}
              fill="none"
              stroke="#ffffff"
              strokeWidth="0.5"
            />
            <polygon points={starPoints(15, 9.6, 2.2, 0.9, 5)} fill="#ffffff" />
          </>
        )}

        {flag.extra === "amazigh" && (
          // the red Yaz, from a 900x600 drawing
          <g transform="scale(0.03333)" fill="#cc0033">
            <polygon points="429.7,477.6 458.1,507.7 466.3,98 450,80.9" />
            <path d="M657.3 515.9l54.5-23.6c-104.9-104.1-165-142.3-264.2-144.7-126 8.9-208.1 59.3-243.1 159.3l24.4-4.9c100-123.6 156.9-109.8 220.3-117.1 67.5 2.4 136.6 46.3 208.1 130.9z" />
            <path d="M289.8 93.1l-52 13.8c50.4 89.4 122.8 143.9 215.4 148 122.8.8 193.5-82.1 242.3-156.9l-41.5 12.2c-78.9 111.4-164.2 115.4-202.4 109.8-64.2-4.9-117.1-57.7-161.8-126.8z" />
          </g>
        )}

        {flag.extra === "assyrian" && (
          // wavy rivers to the corners around a sun and star (900x600 drawing)
          <g transform="scale(0.03333)">
            <path
              fill="#003e87"
              d="M168 0C174 0 178 13 180 18C186 32 198 41 211 48C224 55 240 58 251 69C264 81 271 98 279 113C282 120 288 126 294 132C298 135 301 139 306 142C317 149 330 156 337 167C343 177 344 189 350 198C356 209 368 215 377 223C386 230 389 243 397 251L429 280C435 286 438 280 439 279C440 276 436 272 429 269C427 268 426 267 424 266C413 257 409 244 406 232C404 222 400 214 394 208C389 203 382 200 377 195C370 187 366 177 364 167C362 156 357 147 349 139C342 130 330 126 323 117C316 109 314 98 309 88C306 82 305 75 302 69C299 62 295 55 292 48C290 42 287 36 282 33C269 27 256 20 245 10C242 6 238 5 236 0ZM663 0C661 5 657 6 653 10C643 20 630 27 617 33C611 36 609 42 606 48C604 55 600 62 597 69C594 75 593 82 590 88C585 98 583 109 576 117C568 126 557 130 549 139C542 147 537 156 534 167C532 177 528 187 521 195C517 200 510 203 505 208C499 214 494 222 492 232C490 244 485 257 475 266C473 267 471 268 470 269C463 272 459 276 460 279C461 280 464 286 470 280L501 251C510 243 512 230 521 223C531 215 542 209 548 198C554 189 556 177 562 167C569 156 581 149 593 142C597 139 601 135 605 132C611 126 616 120 620 113C628 98 635 81 648 69C659 58 674 55 687 48C700 41 712 32 718 18C720 13 724 0 730 0ZM432 305C430 305 427 306 425 307C420 311 410 321 397 322C382 323 379 345 366 352C355 359 340 362 330 372C320 380 316 392 306 400C296 408 283 410 272 413C261 415 252 422 244 429C236 436 232 445 224 452C209 466 190 476 170 482C162 484 155 486 147 489C139 492 132 496 125 500C108 509 95 525 86 541C80 551 72 559 62 565C49 572 35 577 20 581C11 584 3 587 0 600L109 600C115 584 126 567 138 555C150 543 166 533 182 527C194 523 205 519 215 513C233 502 244 485 253 467C260 453 274 442 289 438C304 434 320 428 328 414C331 407 333 401 337 396C347 383 365 380 377 370C386 363 387 350 395 341C401 334 417 328 424 321C427 319 439 311 439 307C439 306 436 305 432 305ZM468 305C464 305 461 306 461 307C461 311 473 319 476 321C483 328 499 334 505 341C513 350 514 363 523 370C535 380 553 383 563 396C567 401 569 407 572 414C580 428 596 434 611 438C626 442 640 453 647 467C656 485 667 502 685 513C695 519 706 523 718 527C734 533 750 543 762 555C774 567 785 584 791 600L900 600C897 587 889 584 880 581C865 577 851 572 838 565C828 559 820 551 814 541C805 525 792 509 775 500C768 496 761 492 753 489C745 486 738 484 730 482C710 476 691 466 676 452C668 445 664 436 656 429C648 422 639 415 628 413C617 410 604 408 594 400C584 392 580 380 570 372C560 362 545 359 534 352C521 345 518 323 503 322C490 321 480 311 475 307C473 306 470 305 468 305Z"
            />
            <path
              fill="#de0000"
              d="M-1 0C2 13 9 16 19 19C33 23 47 28 60 35C71 41 79 49 85 59C94 75 107 91 123 100C130 104 138 108 146 111C153 114 161 116 169 118C189 124 207 134 223 148C230 155 234 164 242 171C250 178 260 185 270 187C282 190 294 192 304 200C314 208 319 220 328 228C338 238 354 241 364 248C377 255 381 277 396 278C409 279 418 289 424 293C429 297 438 294 438 293C438 289 425 281 423 279C415 272 400 266 394 259C386 250 384 237 376 230C364 220 346 217 336 204C332 199 330 193 326 187C319 172 303 166 288 162C272 158 259 147 252 133C243 115 231 98 214 87C203 81 192 77 181 73C165 67 149 57 137 45C124 33 114 16 108 0ZM791 0C785 16 774 33 762 45C750 57 734 67 718 73C706 77 695 81 685 87C667 98 656 115 647 133C640 147 626 158 611 162C596 166 580 172 572 187C569 193 567 199 563 204C553 217 535 220 523 230C514 237 513 250 505 259C499 266 483 272 476 279C473 281 461 289 461 293C461 294 470 297 475 293C480 289 490 279 503 278C518 277 521 255 534 248C545 241 560 238 570 228C580 220 584 208 594 200C604 192 617 190 628 187C639 185 648 178 656 171C664 164 668 155 676 148C691 134 710 124 730 118C738 116 745 114 753 111C761 108 768 104 775 100C792 91 805 75 814 59C820 49 828 41 838 35C851 28 865 23 880 19C889 16 897 13 900 0ZM435 317C434 318 432 318 430 320L399 349C390 357 388 370 379 377C369 385 358 391 352 402C346 411 344 423 338 433C331 444 319 451 307 458C303 461 299 465 295 468C289 474 284 480 280 487C272 502 265 519 252 531C241 542 226 545 213 552C200 559 188 568 182 582C180 587 176 600 170 600L237 600C239 595 243 594 247 590C257 580 270 573 283 567C289 564 291 558 294 552C296 545 300 538 303 531C306 525 307 518 310 512C315 502 317 491 324 483C332 474 343 470 351 462C358 453 363 444 366 433C368 423 372 413 379 405C383 400 390 397 395 392C401 386 406 378 408 368C410 356 415 343 425 334C427 333 429 332 430 331C437 328 441 324 440 321C440 320 438 317 435 317ZM465 317C462 317 460 320 460 321C459 324 463 328 470 331C471 332 473 333 475 334C485 343 490 356 492 368C494 378 499 386 505 392C510 397 517 400 521 405C528 413 532 423 534 433C537 444 542 453 549 462C557 470 568 474 576 483C583 491 585 502 590 512C593 518 594 525 597 531C600 538 604 545 606 552C609 558 611 564 617 567C630 573 643 580 653 590C657 594 661 595 663 600L730 600C724 600 720 587 718 582C712 568 700 559 687 552C674 545 659 542 648 531C635 519 628 502 620 487C616 480 611 474 605 468C601 465 597 461 593 458C581 451 569 444 562 433C556 423 554 411 548 402C542 391 531 385 521 377C512 370 510 357 501 349L470 320C468 318 466 318 465 317Z"
            />
            <path fill="#42a7ea" d="M475,325 450,405 425,325 345,300 425,275 450,195 475,275 555,300Z" />
            <circle cx="450" cy="300" r="29" fill="#eebf19" />
          </g>
        )}

        {flag.extra === "druze" && <polygon points="0,0 10,10 0,20" fill="#00804a" />}

        {flag.extra === "uyghur" && (
          // white crescent and star on sky blue (900x600 drawing)
          <path
            transform="translate(0 10) scale(0.000333)"
            fill="#ffffff"
            d="m41750 0 13568-4408-8386 11541V-7133l8386 11541zm925 8021a15000 15000 0 1 1 0-16042 12000 12000 0 1 0 0 16042"
          />
        )}

        {flag.extra === "tibet" && (
          // simplified: rays, sun, and snowy mountain inside a gold border
          <>
            {Array.from({ length: 12 }, (_, i) => {
              const a0 = ((i * 30 - 105) * Math.PI) / 180;
              const a1 = (((i + 1) * 30 - 105) * Math.PI) / 180;
              return (
                <polygon
                  key={i}
                  points={`15,11.5 ${15 + 40 * Math.cos(a0)},${11.5 + 40 * Math.sin(a0)} ${
                    15 + 40 * Math.cos(a1)
                  },${11.5 + 40 * Math.sin(a1)}`}
                  fill={i % 2 ? "#1d2c8a" : "#d52b1e"}
                />
              );
            })}
            <polygon points="4,20 15,12.8 26,20" fill="#ffffff" />
            <circle cx="15" cy="10.8" r="2.4" fill="#ffd200" stroke="#ffffff" strokeWidth="0.5" />
            <path d="M0,0 H30 V0.9 H0.9 V19.1 H30 V20 H0 Z" fill="#ffd200" />
          </>
        )}

        {flag.extra === "circassian" && (
          // twelve gold stars (an arc of nine over a row of three) and three crossed arrows
          <g fill="#ffd200">
            {Array.from({ length: 9 }, (_, i) => {
              const a = ((200 + i * 17.5) * Math.PI) / 180;
              return (
                <polygon key={i} points={starPoints(15 + 7 * Math.cos(a), 12 + 7 * Math.sin(a), 0.9, 0.36, 5)} />
              );
            })}
            {[12.4, 15, 17.6].map((x) => (
              <polygon key={x} points={starPoints(x, 10.3, 0.9, 0.36, 5)} />
            ))}
            <g stroke="#ffd200" strokeWidth="0.45" strokeLinecap="round">
              <line x1="15" y1="18.8" x2="15" y2="12.6" />
              <line x1="11.8" y1="18.4" x2="18" y2="12.9" />
              <line x1="18.2" y1="18.4" x2="12" y2="12.9" />
            </g>
            <polygon points="15,12.1 14.4,13.2 15.6,13.2" />
            <polygon points="18.4,12.5 17.3,12.8 18.1,13.6" />
            <polygon points="11.6,12.5 12.7,12.8 11.9,13.6" />
          </g>
        )}

        {flag.extra === "acadian" && (
          <polygon points={starPoints(5, 6.3, 3.4, 1.3, 5)} fill="#ffd700" />
        )}

        {flag.extra === "cornish" && (
          <g fill="#ffffff">
            <rect x="0" y="8" width="30" height="4" />
            <rect x="13" y="0" width="4" height="20" />
          </g>
        )}

        {flag.extra === "breton" && (
          // white canton with eleven ermine spots (4, 3, 4)
          <>
            <rect x="0" y="0" width="12.6" height="11.12" fill="#ffffff" />
            {[
              [1.9, 2.2], [4.8, 2.2], [7.7, 2.2], [10.6, 2.2],
              [3.35, 5.3], [6.25, 5.3], [9.15, 5.3],
              [1.9, 8.4], [4.8, 8.4], [7.7, 8.4], [10.6, 8.4],
            ].map(([x, y]) => (
              <g key={`${x}-${y}`} fill="#000000">
                <polygon points={`${x - 0.65},${y + 1.5} ${x},${y - 0.1} ${x + 0.65},${y + 1.5}`} />
                <circle cx={x - 0.55} cy={y - 0.35} r="0.28" />
                <circle cx={x} cy={y - 0.7} r="0.28" />
                <circle cx={x + 0.55} cy={y - 0.35} r="0.28" />
              </g>
            ))}
          </>
        )}

        {flag.extra === "leatherHeart" && (
          // red heart in the upper hoist corner
          <path d="M5.5,7.6 C2.4,5.4 2.6,2.4 4.3,2.4 C5.1,2.4 5.5,3 5.5,3.5 C5.5,3 5.9,2.4 6.7,2.4 C8.4,2.4 8.6,5.4 5.5,7.6 Z" fill="#e40303" />
        )}

        {flag.extra === "bearPaw" && (
          // black paw print in the upper hoist corner
          <g fill="#000000">
            <ellipse cx="5.2" cy="6.4" rx="2" ry="1.6" />
            <circle cx="2.9" cy="4.3" r="0.7" />
            <circle cx="4.3" cy="3.3" r="0.7" />
            <circle cx="6.1" cy="3.3" r="0.7" />
            <circle cx="7.5" cy="4.3" r="0.7" />
          </g>
        )}

        {(flag.extra === "ribbonRed" || flag.extra === "ribbonGreen") && (
          // an awareness ribbon: a loop with two crossing tails
          <path
            d="M11.8,18 L16.3,8.2 C17.6,5.2 16.6,2.6 15,2.6 C13.4,2.6 12.4,5.2 13.7,8.2 L18.2,18"
            fill="none"
            stroke={flag.extra === "ribbonRed" ? "#d0021b" : "#3fa34d"}
            strokeWidth="2.2"
            strokeLinejoin="round"
          />
        )}

        {flag.extra === "spoon" && (
          <g transform="rotate(-35 15 10)" fill="#8a8f98">
            <ellipse cx="15" cy="5.6" rx="2.5" ry="3.3" />
            <ellipse cx="14.4" cy="4.8" rx="0.8" ry="1.4" fill="#c7cbd1" />
            <rect x="14.3" y="8.4" width="1.4" height="9.4" rx="0.7" />
          </g>
        )}

        {flag.extra === "jewishPride" && (
          <g fill="none" stroke="#ffffff" strokeWidth="1.1" strokeLinejoin="round">
            <polygon points="15,4.6 19.7,12.7 10.3,12.7" />
            <polygon points="15,15.4 19.7,7.3 10.3,7.3" />
          </g>
        )}

        {flag.extra === "kurdistan" && (
          <>
            <polygon points={starPoints(15, 10, 5.4, 3, 21)} fill="#febd11" />
            <circle cx="15" cy="10" r="3.1" fill="#febd11" />
          </>
        )}
      </g>
    </svg>
  );
}

// One flag chip (pride or country), with its name as a tooltip.
export function Flag({ id, height = 18 }: { id: string; height?: number }) {
  const info = flagInfo(id);
  if (!info) return null;
  return (
    <span
      className="flag-chip"
      style={{ height, width: Math.round(height * 1.5) }}
      title={info.name}
      role="img"
      aria-label={info.name}
    >
      {info.kind === "pride" ? (
        <PrideSvg flag={info.flag} />
      ) : (
        <span className={`fi fi-${info.code}`} />
      )}
    </span>
  );
}

export function FlagRow({ ids, height = 18 }: { ids: string[]; height?: number }) {
  if (ids.length === 0) return null;
  return (
    <span className="flag-row" aria-label="Flags">
      {ids.map((id) => (
        <Flag key={id} id={id} height={height} />
      ))}
    </span>
  );
}

const GROUP_LABELS: Record<FlagGroup, string> = {
  pride: "Pride",
  indigenous: "Indigenous",
  community: "Disability and neurodivergent",
  heritage: "Heritage",
};

const TABS: [FlagGroup | "country", string][] = [
  ["pride", "Pride"],
  ["indigenous", "Indigenous"],
  ["community", "Community"],
  ["heritage", "Heritage"],
  ["country", "Countries"],
];

// Pick up to 5 flags: drawn flags as a grid by group, countries with a search box.
export function FlagPicker({
  value,
  onChange,
}: {
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const [tab, setTab] = useState<FlagGroup | "country">("pride");
  const [search, setSearch] = useState("");
  const full = value.length >= MAX_FLAGS;

  const toggle = (id: string) => {
    if (value.includes(id)) onChange(value.filter((v) => v !== id));
    else if (!full) onChange([...value, id]);
  };

  const countries = useMemo(() => {
    const term = search.trim().toLowerCase();
    return term ? COUNTRIES.filter((c) => c.name.toLowerCase().includes(term)) : COUNTRIES;
  }, [search]);

  return (
    <div className="flex flex-col gap-3">
      <div className="field">
        <span className="field-label">
          Your flags ({value.length}/{MAX_FLAGS})
        </span>
        {value.length === 0 ? (
          <p className="text-sm text-ink-soft">None yet. Pick up to {MAX_FLAGS} below.</p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {value.map((id) => (
              <li key={id} className="chip on gap-2">
                <Flag id={id} height={16} />
                {flagInfo(id)?.name}
                <button
                  type="button"
                  className="leading-none"
                  aria-label={`Remove ${flagInfo(id)?.name}`}
                  onClick={() => toggle(id)}
                >
                  <Icon name="close" size={12} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="chip-row" role="group" aria-label="Kind of flag">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`chip !px-3 !py-1 !text-sm ${tab === key ? "on" : ""}`}
            aria-pressed={tab === key}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {full && (
        <p className="text-sm text-ink-soft">
          That's {MAX_FLAGS}! Remove one to pick another.
        </p>
      )}

      {tab !== "country" ? (
        <div className="flag-grid" role="group" aria-label={`${GROUP_LABELS[tab]} flags`}>
          {groupList(tab).map(({ id, label }) => {
            const on = value.includes(id);
            return (
              <button
                key={id}
                type="button"
                className="pick thumb"
                aria-pressed={on}
                disabled={!on && full}
                onClick={() => toggle(id)}
              >
                <Flag id={id} height={24} />
                <span className="thumb-label">{label}</span>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <input
            className="input"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search countries and regions..."
            aria-label="Search countries and regions"
          />
          <div className="flag-list" role="group" aria-label="Countries and regions">
            {countries.map((c) => {
              const id = `c:${c.code}`;
              const on = value.includes(id);
              return (
                <button
                  key={id}
                  type="button"
                  className="flag-list-item"
                  aria-pressed={on}
                  disabled={!on && full}
                  onClick={() => toggle(id)}
                >
                  <Flag id={id} height={16} />
                  <span className="min-w-0 flex-1 text-left">{c.name}</span>
                  {on && <Icon name="check" size={14} tone="pink" />}
                </button>
              );
            })}
            {countries.length === 0 && (
              <p className="p-2 text-sm text-ink-soft">No matches for "{search}".</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
